import { useEffect } from 'react';
import {
  SCIENCE_INTRO,
  HYPOTHESIS,
  EXPERIMENT_INTRO,
  EXPERIMENT_STEPS,
  RESULTS_INTRO,
  RESULTS_TABLE,
  PRACTICAL_RESEARCH,
} from '../data/scienceContent.js';

export default function MoreInfo() {
  useEffect(() => {
    document.title = 'More Info | Soap Showdown';
  }, []);

  return (
    <main className="mx-auto max-w-3xl px-5 py-10 sm:py-16">
      <a
        href="/"
        className="text-sm font-bold uppercase tracking-wide text-showdown-pink hover:underline focus-visible:underline"
      >
        &larr; Back to Soap Showdown
      </a>

      <h1 className="mt-4 text-3xl font-extrabold text-showdown-blue sm:text-4xl">More Info</h1>

      <Section heading="Hypothesis" accent="blue">
        <p>{HYPOTHESIS}</p>
      </Section>

      <Section heading="Experiment" accent="green">
        <p>{EXPERIMENT_INTRO}</p>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5">
          {EXPERIMENT_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </Section>

      <Section heading="Results" accent="yellow">
        <p className="font-bold text-showdown-pink">{RESULTS_INTRO}</p>

        {/* Cards on narrow screens — a 3-column table doesn't fit without a
            hidden/non-obvious horizontal scroll, so stack instead. */}
        <ul className="mt-4 flex flex-col gap-3 sm:hidden">
          {RESULTS_TABLE.map((row) => (
            <li key={row.ingredient} className="rounded-2xl bg-black/30 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-bold">{row.ingredient}</span>
                <span className="shrink-0 font-bold text-showdown-pink">{row.reaction}</span>
              </div>
              <p className="mt-1 text-showdown-cream/80">{row.result}</p>
            </li>
          ))}
        </ul>

        {/* Real table from sm+, where 560px comfortably fits with no scroll. */}
        <div className="mt-4 hidden sm:block">
          <table className="w-full border-collapse text-left text-base">
            <caption className="sr-only">
              Results table: ingredient, observed result, and reaction score out of 10
            </caption>
            <thead>
              <tr className="border-b border-showdown-cream/20 text-xs uppercase tracking-wide text-showdown-cream/60">
                <th scope="col" className="px-3 py-2">
                  Ingredient
                </th>
                <th scope="col" className="px-3 py-2">
                  Result
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  Reaction
                </th>
              </tr>
            </thead>
            <tbody>
              {RESULTS_TABLE.map((row) => (
                <tr key={row.ingredient} className="border-b border-showdown-cream/10 align-top">
                  <th scope="row" className="px-3 py-3 font-semibold">
                    {row.ingredient}
                  </th>
                  <td className="px-3 py-3 text-showdown-cream/80">{row.result}</td>
                  <td className="px-3 py-3 text-right font-bold text-showdown-pink">{row.reaction}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section heading={`Research: ${SCIENCE_INTRO.heading}`} accent="blue">
        <p>{SCIENCE_INTRO.summary}</p>
      </Section>

      <Section heading="Practical Research" accent="green">
        <p>{PRACTICAL_RESEARCH}</p>
      </Section>

      <a
        href="/"
        className="mt-10 inline-block rounded-full bg-showdown-pink px-6 py-3 text-sm font-extrabold uppercase tracking-wide text-showdown-ink shadow-lg transition
                   hover:bg-showdown-pink-dark focus-visible:outline focus-visible:outline-4 focus-visible:outline-showdown-yellow"
      >
        &larr; Back to Soap Showdown
      </a>
    </main>
  );
}

const ACCENT_TEXT = {
  blue: 'text-showdown-blue',
  green: 'text-showdown-green',
  yellow: 'text-showdown-yellow',
};

function Section({ heading, accent = 'blue', children }) {
  return (
    <section className="mt-6 rounded-3xl border border-showdown-cream/10 bg-showdown-ink p-6 shadow-xl sm:mt-8 sm:p-8">
      <h2 className={`text-xl font-extrabold sm:text-2xl ${ACCENT_TEXT[accent]}`}>{heading}</h2>
      <div className="mt-3 space-y-3 text-base leading-relaxed text-showdown-cream/85 sm:text-lg">{children}</div>
    </section>
  );
}
